import React, { useEffect, useState } from 'react';
import { loadAgentAssets, buildAndQuery } from './promptBuilder';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import './agentRunner.css';

type Props = {
  agentName: string;
  inputContext: any;
  onClose: () => void;
};

const AgentRunner: React.FC<Props> = ({ agentName, inputContext, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [assistantText, setAssistantText] = useState<string | null>(null);
  const [assets, setAssets] = useState<any>(null);
  const [acceptedActions, setAcceptedActions] = useState<Record<string, boolean>>({});
  const [actionResponses, setActionResponses] = useState<Record<string, string>>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [modalStatus, setModalStatus] = useState<number | null>(null);
  const [modalRawBody, setModalRawBody] = useState<string | null>(null);
  const [modalRequestBody, setModalRequestBody] = useState<string | null>(null);
  const [tokenUsage, setTokenUsage] = useState<any | null>(null);
  const [tokenModel, setTokenModel] = useState<string | null>(null);
  const [planText, setPlanText] = useState<string | null>(null);
  const [aiRecommendedActions, setAiRecommendedActions] = useState<Array<any>>([]);
  const [copiedPrompt, setCopiedPrompt] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const a = await loadAgentAssets(agentName);
        if (!mounted) return;
        setAssets(a);
        try {
          const baseApiRaw = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001');
          const baseApi = baseApiRaw.replace(/\/+$/, '');
          const inputUrl = baseApi.endsWith('/api') ? `${baseApi}/agents/input_context` : `${baseApi}/api/agents/input_context`;
          const icRes = await fetch(inputUrl);
          const icJson = await icRes.json();
          if (icRes.ok && icJson?.data) { inputContext = icJson.data; }
        } catch (e) { /* fall back to provided inputContext prop */ }

        const { prompt: built, resp } = await buildAndQuery(agentName, inputContext);
        if (!mounted) return;
        setPrompt(built);

        if (resp?.mock) {
          setAssistantText(null);
          setPlanText(built);
          setAiRecommendedActions([]);
          setLoading(false);
          return;
        }

        const text = resp?.choices?.[0]?.message?.content ?? JSON.stringify(resp);
        setAssistantText(text);
        try {
          const usage = resp?.usage ?? resp?.data?.usage ?? null;
          if (usage) setTokenUsage(usage);
          const modelName = resp?.model ?? resp?.data?.model ?? null;
          if (modelName) setTokenModel(modelName);
        } catch (e) { /* ignore */ }

        try {
          let parsed: any = null;
          const jsonStart = text.indexOf('{');
          const jsonEnd = text.lastIndexOf('}');
          if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
            parsed = JSON.parse(text.slice(jsonStart, jsonEnd + 1));
          }
          if (!parsed) {
            const lines = text.split(/\r?\n/).map((l: string) => l.trim()).filter(Boolean);
            let plan = '';
            const actions: any[] = [];
            if (lines.length) {
              if (/^PLAN[:|]/i.test(lines[0])) { plan = lines[0].replace(/^PLAN[:|]\s*/i, ''); lines.shift(); }
              else { while (lines.length && !/^\d+\|/.test(lines[0])) { plan += (plan ? ' ' : '') + lines.shift(); } }
            }
            lines.forEach((l: string) => {
              const m = l.match(/^\s*(\d+)\|([^|]+)\|([^|]+)\|(.+)$/);
              if (m) actions.push({ id: Number(m[1]), code: m[2].trim(), text: m[3].trim(), reason: m[4].trim() });
            });
            if (actions.length || plan) parsed = { plan, recommended_actions: actions };
          }
          if (parsed) {
            setPlanText(parsed.plan ?? null);
            setAiRecommendedActions(parsed.recommended_actions ?? parsed.recommendedActions ?? []);
          } else {
            setPlanText(text);
            setAiRecommendedActions([]);
          }
        } catch (e) {
          setPlanText(text);
          setAiRecommendedActions([]);
        }
      } catch (err: any) {
        setError(err?.message || String(err));
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [agentName, JSON.stringify(inputContext)]);

  async function executeAndAccept(id: number | string) {
    const key = String(id);
    setAcceptedActions((s) => ({ ...s, [key]: false }));
    setModalTitle(`Execute API (${key})`);
    setModalMessage('Preparing to execute...');
    setModalOpen(true);
    try {
      const baseApiRaw = (process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001');
      const baseApi = baseApiRaw.replace(/\/+$/, '');
      const url = baseApi.endsWith('/api') ? `${baseApi}/agents/execute_action` : `${baseApi}/api/agents/execute_action`;
      const reqBody = JSON.stringify({ id: Number(id) });
      setModalRequestBody(reqBody);
      setModalMessage('Executing...');
      setModalUrl(url);
      setModalStatus(null);
      setModalRawBody(null);
      const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: reqBody });
      let j: any = null;
      try {
        j = await resp.json();
        setModalStatus(resp.status);
        setModalRawBody(JSON.stringify(j));
      } catch (parseErr) {
        const txt = await resp.text();
        j = { message: txt };
        setModalStatus(resp.status || null);
        setModalRawBody(txt);
      }
      if (!resp.ok) throw new Error(j?.error || j?.message || 'Execution failed');
      setAcceptedActions((s) => ({ ...s, [key]: true }));
      const msg = j?.message ?? 'Executed';
      setActionResponses((s) => ({ ...s, [key]: msg }));
      setModalMessage(msg);
    } catch (err: any) {
      const em = String(err?.message ?? err);
      setActionResponses((s) => ({ ...s, [key]: em }));
      setModalMessage(em);
      if (!modalUrl) setModalUrl((process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001') + '/api/agents/execute_action');
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg">
        <Modal.Header>
          <span className="agent-intro" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <div className="agent-icon-circle" aria-hidden>
              <span className="robot">🤖</span>
            </div>
            <div className="agent-intro-text">
              <div className="agent-title">
                Hi — I am {assets?.instruction?.title ? assets.instruction.title : agentName.replace(/_/g, ' ')}.
              </div>
              <div className="agent-subtitle">
                {assets?.instruction?.description ?? 'I will help you analyse the entity and propose planning recommendations.'}
              </div>
            </div>
            <div className="ai-badge">AI-generated — Exercise caution</div>
          </span>
        </Modal.Header>

        <Modal.Body>
          {loading && <div className="agent-runner-loading">Running analysis…</div>}
          {error && <div className="agent-runner-error">Error: {error}</div>}

          {!loading && !error && (
            <div className="agent-runner-body">
              <section>
                <h4>Plan</h4>
                <pre className="agent-understanding">{planText ?? 'No plan provided'}</pre>
                {(tokenUsage || tokenModel) && (
                  <div className="token-usage">
                    {tokenModel && <div><strong>Model:</strong> {tokenModel}</div>}
                    {tokenUsage && (
                      <div>
                        <strong>Tokens:</strong>
                        {' '}prompt {(tokenUsage.prompt_tokens ?? tokenUsage.prompt) ?? 'n/a'},
                        {' '}completion {(tokenUsage.completion_tokens ?? tokenUsage.completion) ?? 'n/a'},
                        {' '}total {(tokenUsage.total_tokens ?? tokenUsage.total) ?? 'n/a'}
                      </div>
                    )}
                  </div>
                )}
              </section>

              {aiRecommendedActions && aiRecommendedActions.length > 0 && (
                <section>
                  <h4>AI recommended actions</h4>
                  <div className="recommendations-list">
                    {aiRecommendedActions.map((r: any) => (
                      <div key={r.id} className="recommendation-row">
                        <div className="rec-name">{`(${r.id}) ${r.code ? r.code + ' — ' : ''}${r.text || r.name}`}</div>
                        <div className="rec-actions">
                          <Button variant={acceptedActions[String(r.id)] ? 'update' : 'fill'} type="button" onClick={() => executeAndAccept(r.id)} data-testid={`BTN_agent_runner_ai_action_${r.id}_accept`}>
                            {acceptedActions[String(r.id)] ? 'Accepted' : 'Accept'}
                          </Button>
                          <Button variant="outline" type="button" onClick={() => { /* reject - no-op */ }} data-testid={`BTN_agent_runner_ai_action_${r.id}_reject`}>Reject</Button>
                        </div>
                        {actionResponses[String(r.id)] && (
                          <div className="action-response">{actionResponses[String(r.id)]}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h4>Performable actions</h4>
                <div className="recommendations-list">
                  {(assets?.perform_recommendations?.actions ?? []).map((r: any) => (
                    <div key={r.id} className="recommendation-row">
                      <div className="rec-name">{`(${r.id}) ${r.code ? r.code + ' — ' : ''}${r.name}`}</div>
                      <div className="rec-actions">
                        <Button variant={acceptedActions[String(r.id)] ? 'update' : 'fill'} type="button" onClick={() => executeAndAccept(r.id)} data-testid={`BTN_agent_runner_action_${r.id}_accept`}>
                          {acceptedActions[String(r.id)] ? 'Accepted' : 'Accept'}
                        </Button>
                        <Button variant="outline" type="button" onClick={() => { /* reject - no-op */ }} data-testid={`BTN_agent_runner_action_${r.id}_reject`}>Reject</Button>
                      </div>
                      {actionResponses[String(r.id)] && (
                        <div className="action-response">{actionResponses[String(r.id)]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              <div className="agent-runner-footer">
                <small>Accepting an action will queue it for execution (not implemented).</small>
              </div>

              <section className="agent-prompt-footnote">
                <h4>Full prompt sent to OpenAI</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    title="Copy full prompt"
                    onClick={() => {
                      if (!prompt) return;
                      navigator.clipboard.writeText(prompt).then(() => {
                        setCopiedPrompt(true);
                        setTimeout(() => setCopiedPrompt(false), 1800);
                      });
                    }}
                    style={{ cursor: 'pointer', padding: '6px 8px', borderRadius: 6 }}
                    data-testid="BTN_agent_runner_copy_prompt"
                  >
                    📋 Copy prompt
                  </button>
                  {copiedPrompt && <span style={{ color: '#1976d2' }}>Copied!</span>}
                </div>
                <pre className="full-prompt">{prompt ?? 'Prompt not available'}</pre>
              </section>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Executor debug modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
        <Modal.Header>{modalTitle}</Modal.Header>
        <Modal.Body>
          <p className="modal-main">{modalMessage}</p>
          <div className="modal-debug">
            <div><strong>URL:</strong> {modalUrl ?? 'unknown'}</div>
            <div><strong>HTTP status:</strong> {modalStatus ?? 'n/a'}</div>
            <div><strong>Request body:</strong></div>
            <pre className="modal-raw">{modalRequestBody ?? 'n/a'}</pre>
            <div><strong>Raw response / body:</strong></div>
            <pre className="modal-raw">{modalRawBody ?? 'n/a'}</pre>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" type="button" onClick={() => setModalOpen(false)} data-testid="BTN_agent_runner_debug_close">Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AgentRunner;
